import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { trainerScheduleApi, type StudentTrainerSchedule } from '@/api/trainerSchedule';
import { useHasFeature } from '@/hooks/useHasFeature';
import { useAppConfig } from '@/hooks/useAppConfig';
import { useAiSkills } from '@/hooks/useAiSkills';
import { aiSpeakingShown } from '@/lib/aiEntries';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { hasTrainerSection } from '@/lib/storeTabs';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';

/**
 * Расписание тренажёров в календаре ученика.
 *
 * ⚠️ НИЧЕГО НЕ ПОКАЗЫВАЕТ, ПОКА РАСПИСАНИЯ НЕТ. Пустое расписание означает
 * «доступно всегда», и полоса «тренажёры: всегда» была бы шумом на экране,
 * который ученик открывает ради занятий.
 *
 * ⚠️ ЗАКРЫТОЕ ТАРИФОМ СЮДА НЕ ПОПАДАЕТ. Куратор мог задать расписание на весь
 * курс, а у ученика тариф без письма — показать ему «письмо по вторникам»
 * значило бы пообещать то, чего он никогда не увидит.
 */
export function TrainerScheduleStrip({ className }: { className?: string }) {
  const { t } = useTranslation();
  // В приложении — только при разделе тренажёров (то же правило, что у вкладки, `lib/storeTabs.ts`): календарь
  // с 1.1.1 открыт и бесплатнику, а у него тренажёров нет вовсе — запрашивать их расписание незачем.
  const user = useAuthStore((st) => st.user);
  const q = useQuery({
    queryKey: ['student', 'trainer-schedule'],
    queryFn: trainerScheduleApi.mine,
    enabled: !IS_APP_BUILD || hasTrainerSection(user),
  });

  // Хуки нельзя вызывать в цикле, поэтому спрашиваем все четыре сразу.
  const canSpeaking = useHasFeature('ielts_speaking');
  const canWriting = useHasFeature('ielts_writing');
  const canSprint = useHasFeature('sprint');
  const canMock = useHasFeature('mock');
  // «Спикинг по пн/ср/пт» обещал бы тренажёр, который спрятан (D22, 11.09.2026).
  useAppConfig();
  // Каждый навык — своим тумблером, спринт — по своему составу, мок — если ему
  // есть из чего собраться: окно мока ученик видит и до первого мока (12–13.10.2026,
  // docs/CONTRACTS/ai-skills.md).
  const skills = useAiSkills();
  const allowed: Record<string, boolean> = {
    speaking: canSpeaking && aiSpeakingShown() && skills.speaking,
    writing: canWriting && skills.writing,
    sprint: canSprint && skills.sprint_skills.length > 0,
    mock: canMock && skills.mock_skills.length > 0,
  };

  const items = (q.data ?? []).filter((it) => allowed[it.trainer]);
  if (items.length === 0) return null;

  return (
    <section className={cn('rounded-[20px] bg-surface-container p-4', className)}>
      <h2 className="mb-3 font-display text-[15px] font-bold text-on-surface">
        {t('trainerSchedule.studentTitle')}
      </h2>
      <ul className="space-y-2">
        {items.map((it) => (
          <li key={it.trainer} className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-sm font-semibold text-on-surface">
              {t(`trainerSchedule.trainer_${it.trainer}`)}
            </span>
            <span className="text-sm text-on-surface-variant">{describe(it, t)}</span>
            <span
              className={cn(
                'ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-semibold',
                it.open_now
                  ? 'bg-success-container text-success'
                  : 'bg-surface-container-high text-on-surface-variant',
              )}
            >
              {it.open_now
                ? t('trainerSchedule.openToday')
                : it.next_open
                  ? t('trainerSchedule.opensAgain', { date: human(it.next_open) })
                  : t('trainerSchedule.closedByCurator')}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** «пн, ср, пт» и/или «10.09 — 14.09». */
function describe(
  it: StudentTrainerSchedule,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const names = [
    t('weekdaysShort.mon'), t('weekdaysShort.tue'), t('weekdaysShort.wed'),
    t('weekdaysShort.thu'), t('weekdaysShort.fri'), t('weekdaysShort.sat'), t('weekdaysShort.sun'),
  ];
  const parts: string[] = [];
  if (it.weekdays?.length) {
    parts.push([...it.weekdays].sort((a, b) => a - b).map((d) => names[d - 1]).join(', '));
  }
  const from = it.opens_on ? human(it.opens_on) : null;
  const to = it.closes_on ? human(it.closes_on) : null;
  if (from && to) parts.push(`${from} — ${to}`);
  else if (from) parts.push(`${t('trainerSchedule.fromDate')} ${from}`);
  else if (to) parts.push(`${t('trainerSchedule.toDate')} ${to}`);
  return parts.join(' · ');
}

function human(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}` : iso;
}
